import { useState } from 'react';

import { Check, ChevronsUpDown } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCanEnterSandbox } from '@/hooks/useCanEnterSandbox';
import { useCurrentSandbox } from '@/hooks/useCurrentSandbox';
import { useSandboxSwitch } from '@/hooks/useGameSandboxSwitch';
import { Permissions } from '@/models/permission';
import { useSandboxContext } from '@/providers';

import { SandboxExportItem } from './sandboxExportItem';

export const SandboxBreadcrumbDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  // The one source for the scope: the provider the breadcrumb renders under.
  const { selectedSandbox, gameCode } = useSandboxContext();
  const { studioCode = '' } = useParams<{ studioCode: string }>();

  const { switchSandbox } = useSandboxSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const { current: currentSandbox, others } = useCurrentSandbox(
    gameCode,
    selectedSandbox,
  );

  const canEnterSandbox = useCanEnterSandbox();

  const handleSandboxChange = (newSandbox: string) => {
    switchSandbox(studioCode, gameCode, selectedSandbox, newSandbox);
  };

  return (
    <>
      <span className="truncate max-w-48">
        {currentSandbox?.name ?? selectedSandbox}
      </span>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="size-6">
            <ChevronsUpDown />
            <span className="sr-only">Switch sandbox</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-xs p-0">
          <Command>
            <CommandInput placeholder="Search sandbox..." />
            <CommandList className="max-h-48 overflow-y-auto">
              <CommandEmpty>No sandbox found.</CommandEmpty>
              {currentSandbox && (
                <>
                  <CommandGroup heading="Current">
                    <CommandItem
                      value={currentSandbox.name}
                      onSelect={() => {
                        handleSandboxChange(currentSandbox.code);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-100" />
                      {currentSandbox.name}
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup heading="All sandboxes">
                {others.map((sandbox) => (
                  <CommandItem
                    key={sandbox.code}
                    value={sandbox.code}
                    // Same rule as the Sandbox list: a Sandbox no Grant covers
                    // stays visible but cannot be switched to from here either.
                    disabled={!canEnterSandbox(sandbox.code)}
                    onSelect={() => {
                      handleSandboxChange(sandbox.code);
                    }}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    {sandbox.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <PermissionCheck oneOf={[Permissions.sandboxes.export]}>
              <CommandSeparator />
              <CommandGroup className="p-1">
                <SandboxExportItem
                  sandbox={currentSandbox}
                  onExported={() => {
                    setIsOpen(false);
                  }}
                />
              </CommandGroup>
            </PermissionCheck>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
